import { ROUTES } from '#/config/routes';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import RepairRequestStatusBadge from '#/modules/crmMgmt/aftersales/repairRequest/components/RepairRequestStatusBadge';
import {
  readRepairRequestDateRangeFilter,
  resolveRepairRequestDateFilterChipDisplay,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/repairRequestDateListFilter';
import { REPAIR_REQUEST_STATUS_FILTER_OPTIONS } from '#/modules/crmMgmt/aftersales/repairRequest/helper/repairRequestStatus';
import { searchCustomerFilterOptions } from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import { formatDateOrDash } from '#/utils/date';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

// BE sort 白名單：created_at、repair_request_date、repair_request_no、status
export function buildRepairRequestColumns({
  filters = {},
  onFilterChange,
  onRepairRequestDateRangeChange,
  customerLabelCache,
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.repairRequestDetail(row.id),
      getEditPath: (row) => ROUTES.repairRequestEdit(row.id),
      canShowEdit: (row) => row.status === 'DRAFT',
      size: 100,
      minSize: 100,
    }),
    {
      id: 'repairRequestNo',
      header: '報修單號',
      accessorKey: 'repairRequestNo',
      accessorFn: (row) => displayDash(row.repairRequestNo),
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'repairRequestNo',
        label: '報修單號',
        filterValue: filters.repairRequestNo,
        onChange: (value) => onFilterChange('repairRequestNo', value),
        placeholder: '搜尋報修單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 110,
      enableColumnFilter: true,
      cell: ({ row }) => (
        row.original.status
          ? <RepairRequestStatusBadge status={row.original.status} size="sm" />
          : '—'
      ),
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: REPAIR_REQUEST_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      id: 'customerName',
      header: '客戶',
      accessorFn: (row) => displayDash(row.customerName),
      size: 160,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        // 顯示單據客戶名稱快照；篩選走 customer_id 關聯（未關聯客戶的舊單篩不到）
        ...createAsyncMultiFilterMeta({
          key: 'customerId',
          label: '客戶',
          searchOptions: searchCustomerFilterOptions,
          filterValue: filters.customerId,
          onChange: (value) => onFilterChange('customerId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(customerLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(customerLabelCache, options, values);
          },
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'repairRequestDate',
      header: '報修日',
      accessorKey: 'repairRequestDate',
      accessorFn: (row) => formatDateOrDash(row.repairRequestDate),
      size: 148,
      minSize: 148,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'repairRequestDateFrom',
        label: '報修日',
        mode: 'range',
        filterValue: readRepairRequestDateRangeFilter(filters),
        onChange: onRepairRequestDateRangeChange,
        placeholder: '西元年/月/日',
        resolveFilterDisplay: resolveRepairRequestDateFilterChipDisplay,
      }),
    },
    {
      id: 'itemCount',
      header: '項目數',
      accessorFn: (row) => row.itemCount ?? row.lines?.length ?? 0,
      size: 80,
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      id: 'remark',
      header: '備註',
      accessorFn: (row) => displayDash(row.remark),
      size: 200,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ getValue }) => (
        <TruncatedTextWithTooltip text={getValue()} />
      ),
    },
  ];
}
